import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  GENERATE_PROOF_OF_STUDY,
  GENERATE_TRANSCRIPT,
  STUDENT,
  STUDENT_ACCOUNT_INFO,
  STUDENT_ACCOUNT_INFO_WITH_DELETED,
  STUDENT_ACHIEVEMENT_RECORDS,
  STUDENT_ALL,
  STUDENT_CONTACT_INFO,
  STUDENT_DELETE,
  STUDENT_FULL_INFO,
  STUDENT_GUARD_CREATE,
  STUDENT_GUARD_LIST,
  STUDENT_LIST,
  STUDENT_PERSONAL_INFO,
  STUDENT_PUBLIC_STUDY_PROOF,
  STUDENT_PUBLIC_TRANSCRIPT,
  STUDENT_STATUS_UPDATE,
  STUDENT_STUDY_PROOF_GENERATE,
  STUDENT_USERNAME,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const generateProofOfStudy = createAsyncThunk(
  GENERATE_PROOF_OF_STUDY,
  async (params: PayloadCreatorParams<`${typeof API.GENERATE_PROOF_OF_STUDY}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.generateProofOfStudy(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentStudyProofGenerate = createAsyncThunk(
  STUDENT_STUDY_PROOF_GENERATE,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_STUDY_PROOF_GENERATE}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.generateStudyProof(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentPublicStudyProof = createAsyncThunk(
  STUDENT_PUBLIC_STUDY_PROOF,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_PUBLIC_STUDY_PROOF}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentPublicStudyProof(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentPublicTranscript = createAsyncThunk(
  STUDENT_PUBLIC_TRANSCRIPT,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_PUBLIC_TRANSCRIPT}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentPublicTranscript(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const student = createAsyncThunk(
  STUDENT,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.student(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentDelete = createAsyncThunk(
  STUDENT_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.studentDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentList = createAsyncThunk(
  STUDENT_LIST,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentAchievementRecords = createAsyncThunk(
  STUDENT_ACHIEVEMENT_RECORDS,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_ACHIEVEMENT_RECORDS}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentAchievementRecords(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentAll = createAsyncThunk(
  STUDENT_ALL,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_ALL}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.studentAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentGuardCreate = createAsyncThunk(
  STUDENT_GUARD_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_GUARD}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.studentGuardCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentGuardList = createAsyncThunk(
  STUDENT_GUARD_LIST,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_GUARD}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentGuardList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentUsername = createAsyncThunk(
  STUDENT_USERNAME,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_USERNAME}{username}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentUsername(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentPersonalInfo = createAsyncThunk(
  STUDENT_PERSONAL_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_PERSONAL_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.studentPersonalInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentFullInfo = createAsyncThunk(
  STUDENT_FULL_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_FULL_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.studentFullInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentContactInfo = createAsyncThunk(
  STUDENT_CONTACT_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_CONTACT_INFO}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.studentContactInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentAccountInfo = createAsyncThunk(
  STUDENT_ACCOUNT_INFO,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_ACCOUNT_INFO}{username}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.studentAccountInfo(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentAccountInfoWithDeleted = createAsyncThunk(
  STUDENT_ACCOUNT_INFO_WITH_DELETED,
  async (
    params: PayloadCreatorParams<`${typeof API.STUDENT_ACCOUNT_INFO_WITH_DELETED}{username}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.studentAccountInfoWithDeleted(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentStatusUpdate = createAsyncThunk(
  STUDENT_STATUS_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.STUDENT_STATUS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.studentStatusUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const generateTranscript = createAsyncThunk(
  GENERATE_TRANSCRIPT,
  async (params: PayloadCreatorParams<`${typeof API.GENERATE_TRANSCRIPT}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.generateTranscript(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const studentClear = createAction(`${STUDENT}/clear`)

export const studentListClear = createAction(`${STUDENT_LIST}/clear`)

export const studentAchievementRecordsClear = createAction(`${STUDENT_ACHIEVEMENT_RECORDS}/clear`)

export const studentAllClear = createAction(`${STUDENT_ALL}/clear`)

export const studentGuardListClear = createAction(`${STUDENT_GUARD_LIST}/clear`)

export const studentUsernameClear = createAction(`${STUDENT_USERNAME}/clear`)

export const studentPublicTranscriptClear = createAction(`${STUDENT_PUBLIC_TRANSCRIPT}/clear`)

export const studentPublicStudyProofClear = createAction(`${STUDENT_PUBLIC_STUDY_PROOF}/clear`)

export const studentStudyProofGenerateClear = createAction(`${STUDENT_STUDY_PROOF_GENERATE}/clear`)
